import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { NotFound } from '../common/components';
import { useInViewOnce } from '../common/hooks/use-in-view-once';
import { useTheme } from '../common/hooks/use-theme';
import { standbyService, StandbyCard } from './service/standby.service';
import { STANDBY_FIRST_FRAMES, STANDBY_STAGE_BG } from './first-frames';

/**
 * Where the sound starts when someone turns it on.
 *
 * The film's bed already carries its own mixed envelope from the Remotion
 * composition — fade in, sustain, resolve on the closing underline — so this is
 * a multiplier on an already-conservative mix, not a raw level. Full scale on
 * top of that is louder than a film embedded in a page has any business being;
 * 0.65 is present and warm and will not make anyone in an open office flinch.
 */
const DEFAULT_VOLUME = 0.5;

/** Long enough to read as a swell, short enough not to feel broken. */
const VOLUME_RAMP_MS = 350;

const VOLUME_KEY = 'standby_reel_volume';

/**
 * The viewer's own on/off answer for sound, remembered between visits the same
 * way the level is. Absent means they have never said, and sound is on.
 */
const SOUND_KEY = 'standby_reel_sound';

const readSoundChoice = (): 'on' | 'off' | null => {
  try {
    const stored = localStorage.getItem(SOUND_KEY);
    return stored === 'on' || stored === 'off' ? stored : null;
  } catch {
    return null; // private mode
  }
};

const writeSoundChoice = (choice: 'on' | 'off') => {
  try {
    localStorage.setItem(SOUND_KEY, choice);
  } catch {
    /* private mode */
  }
};

/**
 * Grace period before the volume panel closes on pointer-out. Practitioners
 * land between 150 and 500ms for this; below ~200 the panel still escapes a
 * slightly wide approach, above ~500 it reads as stuck open.
 */
const VOLUME_CLOSE_DELAY_MS = 300;

/**
 * Slider position to gain, and back.
 *
 * Squared, because loudness is perceived roughly logarithmically: a linear
 * slider feels inert across its top half and hair-triggered at the bottom.
 * Squaring spreads the useful range evenly along the travel. The default gain
 * of 0.65 therefore sits at position 0.81, not 0.65.
 */
const gainFromSlider = (p: number) => p * p;
const sliderFromGain = (g: number) => Math.sqrt(g);

/**
 * Backed rather than themed. The landing reel's controls are bare white with a
 * drop shadow, which works because that footage is dark — this film is mostly
 * white and they disappeared into it. A dark translucent disc reads over any
 * footage, so the same treatment survives the light cut, the dark cut and
 * anything either becomes later.
 *
 * These do not auto-hide. On the landing page the reel *is* the page and
 * chrome should get out of the way; here the film is one section of something
 * you scroll, and a control that vanishes is a control you have to go hunting
 * for.
 */
const CONTROL_CLASS =
  'flex h-[44px] w-[44px] items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition-colors hover:bg-black/75 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80';

const PlayIcon = ({ playing }: { playing: boolean }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    {playing ? <path d="M8 5h3v14H8zM13 5h3v14h-3z" /> : <path d="M7 4.5l13 7.5-13 7.5z" />}
  </svg>
);

const SpeakerIcon = ({ muted }: { muted: boolean }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 9.5h3.5L12 5.5v13L7.5 14.5H4v-5z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    {muted ? (
      <path d="M16 9.5l5 5m0-5l-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    ) : (
      <path
        d="M15.5 9c1 .9 1.6 1.9 1.6 3s-.6 2.1-1.6 3M18 6.6C19.6 8 20.5 9.9 20.5 12s-.9 4-2.5 5.4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    )}
  </svg>
);

/**
 * Which cut suits the viewport as it is right now.
 *
 * A portrait phone gets the portrait film rather than the landscape one
 * cropped to fit: object-cover on a 16:9 source in a 9:16 viewport throws away
 * most of the frame, and what survives is the middle third of shots that were
 * composed for the full width.
 *
 * This is re-evaluated on rotation and resize. It used to be decided once at
 * mount to avoid restarting playback — but the swap below carries the position
 * across, so there is nothing to protect against: turning the phone changes
 * the framing and keeps the frame.
 */
const pickVariant = (variants: string[]): string => {
  if (typeof window === 'undefined') return variants[variants.length - 1];
  const w = window.innerWidth;
  const h = window.innerHeight;

  // Portrait and phone-sized. A portrait tablet is wide enough that the
  // landscape cut still reads, and it has the screen to show it.
  if (h > w && w <= 820) {
    const wanted = w <= 430 ? 'v960' : 'v1280';
    if (variants.includes(wanted)) return wanted;
  }

  const wanted = w <= 640 ? '540p' : w <= 1280 ? '720p' : '1080p';
  return variants.includes(wanted) ? wanted : variants[variants.length - 1];
};

/** Portrait cuts are framed 9:16; the landscape poster would letterbox them. */
const isVerticalVariant = (variant: string | null) => !!variant?.startsWith('v');

/**
 * Which opening frame to show, decided without waiting for anything.
 *
 * The chosen cut is not known until the page payload names the available
 * variants, but the frame has to be on screen before that — so the orientation
 * is read straight off the viewport, using the same test pickVariant uses. Once
 * the variant is known it wins, because by then it is the authority. The two
 * answers agree in every case except a portrait tablet, where the frame is a
 * near-flat wash either way.
 */
const posterOrientation = (variant: string | null): 'vertical' | 'landscape' => {
  if (variant) return isVerticalVariant(variant) ? 'vertical' : 'landscape';
  if (typeof window === 'undefined') return 'landscape';
  const portraitPhone =
    window.innerHeight > window.innerWidth && window.innerWidth <= 820;
  return portraitPhone ? 'vertical' : 'landscape';
};

const CtaButton: React.FC<{ href: string; label: string; large?: boolean }> = ({
  href,
  label,
  large
}) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer noopener"
    className={`inline-flex items-center gap-2 rounded-full bg-blue font-body font-semibold text-surface-base transition-shadow duration-200 hover:shadow-[0px_0px_0px_4px_rgba(93,225,255,0.25)] ${
      large ? 'px-8 py-4 text-[16px]' : 'px-7 py-3 text-[15px]'
    }`}
  >
    {label}
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M3.5 7h7M8 4.5 10.5 7 8 9.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </a>
);

/** Pill list — the specifics, scannable without reading a sentence. */
const Chips: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="mt-5 flex flex-wrap gap-[6px]">
    {items.map((item) => (
      <li
        key={item}
        className="rounded-full border border-border-dark/[0.1] bg-surface-section px-[11px] py-[5px] font-body text-[12.5px] text-content-muted-alt"
      >
        {item}
      </li>
    ))}
  </ul>
);

/**
 * A card carries an image, a short claim and its specifics as pills. Wide
 * cards put the image beside the copy; the rest stack it on top, which is what
 * lets two sit side by side without the text column getting mean.
 */
const Card: React.FC<{ card: StandbyCard }> = ({ card }) => {
  const [ref, inView] = useInViewOnce<HTMLDivElement>();

  // The art sits on its own fixed ground rather than the card's surface, so it
  // reads identically in light and dark and only had to be designed once. The
  // markup is a static constant authored on the server — nothing user-supplied
  // reaches it.
  const image = (
    <div className="relative flex h-full w-full items-center justify-center overflow-hidden p-7"
      style={{ background: 'linear-gradient(160deg, #182226 0%, #0E1518 100%)' }}>
      <div
        className="h-full w-full opacity-90"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: card.art }}
      />
    </div>
  );

  const copy = (
    <div className="flex flex-1 flex-col justify-center p-7 md:p-9">
      <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-blue">
        {card.eyebrow}
      </div>
      <h3 className="mt-2.5 font-display text-[22px] font-bold leading-[1.2] tracking-[-0.02em] text-content-dark md:text-[26px]">
        {card.heading}
      </h3>
      <p className="mt-2.5 max-w-[46ch] font-body text-[14px] leading-[1.65] text-content-muted-alt">
        {card.body}
      </p>
      <Chips items={card.chips} />
    </div>
  );

  return (
    <div
      ref={ref}
      id={card.id}
      className={`group relative flex overflow-hidden rounded-[18px] border border-border-dark/[0.11] bg-surface-base shadow-[0_1px_3px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.05)] transition-all duration-[250ms] hover:-translate-y-0.5 hover:border-[rgba(14,165,201,0.2)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.1),0_2px_8px_rgba(0,0,0,0.05)] ${
        card.wide ? 'flex-col md:col-span-2 md:flex-row' : 'flex-col'
      } ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      style={{ transitionProperty: 'opacity, transform, box-shadow, border-color' }}
    >
      <span
        aria-hidden="true"
        className="absolute left-0 right-0 top-0 z-10 h-0.5 bg-gradient-to-r from-blue to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
      />
      {card.wide ? (
        <>
          <div className="order-2 md:order-1 md:w-[46%]">{copy}</div>
          <div className="order-1 h-[240px] shrink-0 md:order-2 md:h-auto md:w-[54%]">{image}</div>
        </>
      ) : (
        <>
          <div className="h-[200px] shrink-0">{image}</div>
          {copy}
        </>
      )}
    </div>
  );
};

const StandbyPageContainer: React.FC = () => {
  // The copy and the media still come from the API rather than the bundle, but
  // that is no longer a gate — it is just where the content lives, so it can
  // change without a frontend deploy. Fetched for everyone, signed in or not.
  // The language is part of the key, not just the request: switching languages
  // has to fetch again, and react-query will not do that for a key it has
  // already cached.
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const pageQuery = useQuery({
    queryKey: ['standby-page', lang],
    queryFn: async () => standbyService.getPage(lang),
    /**
     * Keep the copy that is already on screen while the next language loads.
     *
     * The language is part of the key, which is what makes a switch refetch at
     * all — but a new key has no cached data, so without this the page fell
     * back to its loading state, unmounted the whole tree, and took the video
     * element with it. Changing the language stopped the film. Holding the
     * previous data means the only thing that changes is the words.
     */
    placeholderData: (previous) => previous,
    retry: false
  });

  const { isDark } = useTheme();
  const theme: 'light' | 'dark' = isDark ? 'dark' : 'light';

  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  // Read once. Sound is on unless this viewer turned it off on a past visit.
  const [savedSound] = useState(readSoundChoice);
  const [isMuted, setIsMuted] = useState(savedSound === 'off');

  const page = pageQuery.data ?? null;

  /**
   * The media token, pinned to the first one we were given.
   *
   * It is part of the video URL, and this query gets invalidated by the
   * realtime access channel and refetched on window focus. Each refetch mints a
   * fresh token, so reading it straight off `page` meant a background refetch
   * silently produced a different URL — which reassigns src, reloads the
   * element and puts playback back through the browser's autoplay gate, for no
   * reason at all. The token lasts two hours; one is enough for a visit.
   */
  const tokenRef = useRef<string | null>(null);
  if (page && !tokenRef.current) tokenRef.current = page.video.token;
  const token = tokenRef.current;

  // Follows the viewport. The value only moves at the breakpoints, so a slow
  // desktop resize does not churn — it changes once, when the answer changes.
  const [variant, setVariant] = useState<string | null>(null);
  useEffect(() => {
    if (!page) return undefined;

    const choose = () => setVariant(pickVariant(page.video.variants));
    choose();

    // orientationchange fires before iOS has settled the new innerWidth, so
    // resize is what actually carries the correct measurement on a phone.
    window.addEventListener('resize', choose);
    window.addEventListener('orientationchange', choose);
    return () => {
      window.removeEventListener('resize', choose);
      window.removeEventListener('orientationchange', choose);
    };
  }, [page]);


  const wantsSoundRef = useRef(savedSound !== 'off');

  /**
   * Whether the browser will actually let the reel be heard yet.
   *
   * Separate from wanting it. Audible autoplay is refused until the page has
   * been interacted with, so the element stays muted until a gesture arrives
   * however much the viewer wants sound -- and conflating the two is what made
   * the element try to start unmuted, which does not produce sound, it
   * produces a reel that will not play.
   */
  const soundGrantedRef = useRef(false);

  /**
   * Playback level, remembered between visits.
   *
   * Stored as gain, not slider position — the slider converts. Anything the
   * viewer has actually chosen wins over the default; a corrupt or absent value
   * falls back rather than throwing, and out-of-range values are clamped
   * because localStorage is editable by anyone bored enough.
   */
  const [volume, setVolume] = useState<number>(() => {
    try {
      const stored = parseFloat(localStorage.getItem(VOLUME_KEY) ?? '');
      if (Number.isFinite(stored)) return Math.min(1, Math.max(0, stored));
    } catch {
      /* private mode */
    }
    return DEFAULT_VOLUME;
  });

  const [volumeOpen, setVolumeOpen] = useState(false);

  /**
   * Closing the volume popup is delayed, not immediate.
   *
   * The panel floats 10px above the speaker button, and that gap belongs to
   * neither of them: travelling from the button to the slider left the hover
   * target and dismissed the panel before it could be reached, which made the
   * volume unadjustable with a mouse. Two things fix it together — the bridge
   * below removes the dead zone, and this grace period forgives a path that
   * wanders slightly wide of it.
   *
   * 300ms is the usual figure for this: long enough to cover the hop plus a
   * human correction, short enough that a deliberate exit still feels like it
   * closed when you left. Held in a ref rather than state — a re-render here
   * would rebuild the popup subtree mid-hover.
   */
  const closeTimerRef = useRef<number | null>(null);
  /**
   * A mouse drag on a range input does NOT get implicit pointer capture (that
   * is touch only), so dragging the slider and straying off the panel fires
   * pointerleave and would close the panel mid-adjustment. Explicit flag.
   */
  const draggingRef = useRef(false);
  const speakerRef = useRef<HTMLButtonElement>(null);
  const volumeWrapRef = useRef<HTMLDivElement>(null);

  const cancelVolumeClose = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleVolumeClose = () => {
    cancelVolumeClose();
    if (draggingRef.current) return;
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      // Re-checked rather than trusted from closure time: a drag may have
      // started while the timer was pending.
      if (!draggingRef.current) setVolumeOpen(false);
    }, VOLUME_CLOSE_DELAY_MS);
  };

  /**
   * A drag has ended. Whether that should close the panel depends entirely on
   * where the pointer finished, and pointerleave cannot answer it: during a
   * drag the leave either never fired or was deliberately ignored.
   *
   * Asking what is under the pointer covers both endings — released back on
   * the slider after setting a level, which must stay open, and released out
   * in the page after overshooting, which must not. Containment, not the
   * wrapper's own rect: the panel floats outside that box.
   */
  const endVolumeDrag = (clientX: number, clientY: number) => {
    draggingRef.current = false;
    if (!hasHover) return;
    const under = document.elementFromPoint(clientX, clientY);
    if (!under || !volumeWrapRef.current?.contains(under)) scheduleVolumeClose();
  };

  // Belt and braces: the timer must not outlive the component.
  useEffect(() => cancelVolumeClose, []);

  /**
   * Whether this device has a real pointer, deciding how the slider is
   * revealed: hover on a mouse, tap on a phone or iPad. Read once — a device
   * does not grow a mouse mid-session, and matchMedia here is cheaper than
   * guessing from the user agent.
   */
  const [hasHover] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches
  );

  /**
   * Whether the viewer chose to stop the film. Only the pause button sets this.
   *
   * Everything else that pauses a video — a source swap, a backgrounded tab, a
   * browser declining to autoplay — is the platform's doing, not a decision,
   * and the reel is supposed to recover from all of it. Without somewhere to
   * record the difference, "paused" and "chose to pause" are the same state and
   * the page cannot tell whether it is allowed to start playing again.
   */
  const userPausedRef = useRef(false);

  // The held frame during a source swap — see the effect below.
  const freezeRef = useRef<HTMLCanvasElement>(null);
  const [frozen, setFrozen] = useState(false);

  const freezeCurrentFrame = () => {
    const el = videoRef.current;
    const canvas = freezeRef.current;
    if (!el || !canvas || !el.videoWidth) return;
    canvas.width = el.videoWidth;
    canvas.height = el.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // The video is served from the API origin, so this taints the canvas. That
    // is harmless — the frame is only ever displayed, never read back. Guarded
    // anyway: a browser that refuses should cost the swap its polish, not the
    // page its render.
    try {
      ctx.drawImage(el, 0, 0, canvas.width, canvas.height);
    } catch {
      return;
    }
    setFrozen(true);
  };

  /**
   * The src is assigned here rather than in JSX, and that is the whole trick.
   *
   * Assigning src resets a media element to zero. If React owns the attribute
   * it commits the new value before any effect runs, so a swap handler reads
   * currentTime as 0 and "resuming" starts the cut from the top — which is
   * exactly what it was doing. Owning the assignment means the old position is
   * read first, at the instant of the swap, so it is the true frame rather
   * than a sample taken up to a quarter of a second earlier.
   *
   * Both cuts are the same edit at the same length, so that frame is the same
   * frame. The change reads as the film re-lighting, not restarting.
   */
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !variant || !token) return undefined;

    const next = standbyService.videoUrl(theme, variant, token);
    if (el.currentSrc === next || el.getAttribute('src') === next) return undefined;

    const isFirstLoad = !el.getAttribute('src');

    // Muted has to be true in the element's own terms before the source is
    // assigned, because that is when the browser decides whether it is allowed
    // to autoplay. React sets the property; setting defaultMuted puts the
    // attribute there too, which is what WebKit inspects. Cheap insurance, and
    // it survives the element being reset by a later load.
    if (isFirstLoad) {
      el.defaultMuted = true;
      el.muted = true;
    }
    const at = el.currentTime;
    const wasPlaying = !userPausedRef.current;

    // Hold the frame we are on across the swap.
    //
    // Loading a new source drops the element back to its poster until the
    // first frame decodes, so mid-film a swap would flash back to the opening
    // and then jump forward — visibly a video being replaced. Painting the
    // current frame to a canvas over the top means the picture never changes:
    // the film appears to keep running while the source is replaced beneath
    // it. Only for swaps; on first load there is nothing yet to hold.
    if (!isFirstLoad) freezeCurrentFrame();

    /**
     * Ask to play, and keep asking as the element becomes more ready.
     *
     * A rejection here is NOT proof that playback failed. The element also
     * autoplays on its own, and when both fire the explicit call rejects with
     * an interruption — which was being treated as a refusal and left the UI
     * showing a play button over a film that was about to run. The onPlay and
     * onPause handlers already report what is true, so this stays quiet and
     * lets them do it.
     */
    const attemptPlay = () => {
      if (!wasPlaying) return;
      el.muted =
        !soundGrantedRef.current || !wantsSoundRef.current || !atTopRef.current;
      const started = el.play();
      if (started) started.catch(() => undefined);
    };

    const resume = () => {
      // seekable is empty until the index has arrived; without the guard the
      // assignment is silently dropped and the cut starts from the top.
      if (!isFirstLoad && el.seekable.length > 0) el.currentTime = at;
      el.muted =
        !soundGrantedRef.current || !wantsSoundRef.current || !atTopRef.current;
      setIsMuted(!wantsSoundRef.current);
      attemptPlay();
    };

    // Drop the held frame only once the new source is actually showing the
    // same moment, not merely when it has metadata.
    const release = () => setFrozen(false);

    el.addEventListener('loadedmetadata', resume, { once: true });
    // A second attempt once there is data to show. iOS in particular can
    // decline while it still has only metadata and accept moments later.
    el.addEventListener('canplay', attemptPlay);
    el.addEventListener('seeked', release, { once: true });
    el.addEventListener('playing', release, { once: true });
    /**
     * Assigning src is the whole load. There is deliberately no load() call
     * after it, and that omission is the fix for the play button appearing on a
     * fresh visit.
     *
     * Setting the src *attribute* already invokes the resource selection
     * algorithm — the advice to call load() applies to <source> children, which
     * do not. Calling it here meant the browser had usually begun autoplaying
     * off the new src, and load() then aborted that: it sets paused back to
     * true and, because playback had started, fires a pause event. So the
     * control flipped to a play glyph, the film started a moment later off the
     * retry, and the whole thing looked like a video that had to be asked
     * twice. It did — by us.
     */
    el.setAttribute('src', next);
    return () => {
      el.removeEventListener('loadedmetadata', resume);
      el.removeEventListener('canplay', attemptPlay);
      el.removeEventListener('seeked', release);
      el.removeEventListener('playing', release);
    };
  }, [theme, variant, token]);

  /**
   * Sound belongs to the film, and the film is only the top of this page. Once
   * you have scrolled past it you are reading, and a soundtrack you can no
   * longer see playing at you is an intrusion — so it mutes. The choice is
   * remembered, not discarded: scroll back up and it comes on again by itself.
   * Nothing here touches isMuted's meaning as "what the button reports".
   */
  const atTopRef = useRef(true);
  const [stageCovered, setStageCovered] = useState(false);

  useEffect(() => {
    let queued = false;

    const measure = () => {
      queued = false;
      const vh = window.innerHeight;
      const y = window.scrollY;

      const atTop = y < vh * 0.06;
      if (atTop !== atTopRef.current) {
        atTopRef.current = atTop;
        const el = videoRef.current;
        if (el && wantsSoundRef.current) {
          el.muted = !soundGrantedRef.current || !atTop;
          // The button shows the viewer's choice, not the scroll position.
          setIsMuted(!wantsSoundRef.current);
        }
      }

      // Once the story has fully covered the reel there is nothing to see of
      // it, but the browser is still compositing a playing video under every
      // section — the sticky stage's containing block is the whole page, so it
      // never releases. Taking it out of the paint once it is hidden is what
      // keeps the scroll smooth further down.
      setStageCovered(y > vh * 1.05);
    };

    // Scroll fires far faster than the screen refreshes; without this the
    // handler runs several times per frame for no benefit.
    const onScroll = () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  /**
   * Put the film back on whenever it is stopped and nobody asked for that.
   *
   * A single play() call at load time is not enough on any platform, for a
   * different reason on each. Safari on iOS and iPadOS will not start a video
   * it considers off-screen — and it restores your scroll position on reload,
   * so a reload halfway down the page lands on a hidden reel and the request is
   * simply declined; the film was then stuck behind a play button. Desktop
   * browsers decline while a tab is in the background and do not retry when it
   * comes forward. Both are recoverable, and the recovery is the same: notice
   * that it is stopped, and ask again.
   *
   * The retry is bounded by intent, not by a counter — if the viewer pressed
   * pause, this does nothing at all, which is what makes it safe to run often.
   */
  useEffect(() => {
    if (!variant || !theme) return undefined;

    const ensurePlaying = () => {
      const el = videoRef.current;
      if (!el || userPausedRef.current || !el.getAttribute('src')) return;
      // No point asking while the reel is out of the paint — mobile browsers
      // decline for an off-screen video anyway, and scrolling back re-runs this
      // effect, which is the moment the request will actually be granted.
      if (stageCovered || document.hidden || !el.paused) return;
      // Sound is a privilege the film has to re-earn on every attempt: an
      // unmuted request is the one browsers refuse outright.
      el.muted =
        !soundGrantedRef.current || !wantsSoundRef.current || !atTopRef.current;
      const started = el.play();
      if (started) started.catch(() => undefined);
    };

    ensurePlaying();

    const el = videoRef.current;
    // canplay/loadeddata cover a source that only became playable just now;
    // visibilitychange covers the tab coming back to the front.
    el?.addEventListener('canplay', ensurePlaying);
    el?.addEventListener('loadeddata', ensurePlaying);
    document.addEventListener('visibilitychange', ensurePlaying);

    // Last resort: the first thing the viewer does with the page. Low Power
    // Mode on iOS refuses muted autoplay outright and no amount of asking will
    // change that, but a gesture lifts the restriction — and any gesture will
    // do, it does not have to be aimed at the video. Costs nothing when
    // autoplay already worked, since ensurePlaying returns immediately.
    const GESTURES = ['pointerdown', 'touchstart', 'keydown', 'scroll'] as const;
    GESTURES.forEach((type) =>
      document.addEventListener(type, ensurePlaying, { passive: true })
    );

    return () => {
      el?.removeEventListener('canplay', ensurePlaying);
      el?.removeEventListener('loadeddata', ensurePlaying);
      document.removeEventListener('visibilitychange', ensurePlaying);
      GESTURES.forEach((type) => document.removeEventListener(type, ensurePlaying));
    };
    // stageCovered is the dependency that matters: scrolling the reel back into
    // view is exactly when a mobile browser will finally accept the request.
  }, [stageCovered, variant, theme]);

  /**
   * Housekeeping for the slider on touch, where there is no pointer-leave to
   * close it: dismiss on a tap anywhere else, and when the reel scrolls out of
   * view. Without the second one it would still be sitting there, over a video
   * nobody can see, when the viewer scrolls back up.
   */
  useEffect(() => {
    const el = videoRef.current;
    if (el) el.volume = volume;
  }, [volume]);

  // Escape closes it, which WCAG 1.4.13 asks of anything revealed on hover or
  // focus. Focus only returns to the speaker button if focus was inside the
  // control to begin with — yanking it there after a mouse-only hover would
  // move the caret out from under someone who never left the keyboard alone.
  useEffect(() => {
    if (!volumeOpen) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const wrap = document.querySelector('[data-volume-control]');
      const hadFocus = !!wrap && wrap.contains(document.activeElement);
      cancelVolumeClose();
      setVolumeOpen(false);
      if (hadFocus) speakerRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [volumeOpen]);

  useEffect(() => {
    if (!volumeOpen) return undefined;
    if (stageCovered) {
      // Whatever covered the stage wins over a pending grace period.
      cancelVolumeClose();
      setVolumeOpen(false);
      return undefined;
    }
    if (hasHover) return undefined;

    const dismiss = (e: PointerEvent) => {
      const wrap = (e.target as HTMLElement)?.closest?.('[data-volume-control]');
      if (!wrap) setVolumeOpen(false);
    };
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [volumeOpen, stageCovered, hasHover]);

  const togglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      userPausedRef.current = false;
      el.play().catch(() => setIsPlaying(false));
    } else {
      userPausedRef.current = true;
      el.pause();
    }
  };

  /**
   * Bring the sound up rather than switching it on.
   *
   * Unmuting is the jarring moment: the viewer has been watching in silence and
   * a step from nothing to full level is a transient, however sensible the
   * level itself. Easing over a third of a second turns it into a swell. The
   * curve is ease-out — quick to become audible, unhurried settling in — and it
   * cancels cleanly if the viewer mutes again mid-ramp.
   */
  const rampRef = useRef<number | null>(null);

  const rampTo = (target: number) => {
    const el = videoRef.current;
    if (!el) return;
    if (rampRef.current) cancelAnimationFrame(rampRef.current);

    const from = el.volume;
    const start = performance.now();
    const step = (now: number) => {
      // rAF hands over the frame's start time, which can be a moment BEFORE
      // the performance.now() above. Unclamped, the first step eased below
      // zero, the volume setter threw, and the swell died with the reel at 0.
      const p = Math.min(1, Math.max(0, (now - start) / VOLUME_RAMP_MS));
      const eased = 1 - (1 - p) ** 3;
      el.volume = Math.min(1, Math.max(0, from + (target - from) * eased));
      if (p < 1) rampRef.current = requestAnimationFrame(step);
      else rampRef.current = null;
    };
    rampRef.current = requestAnimationFrame(step);
  };

  /** Asked once per visit; see askForSound. */
  const askedRef = useRef(false);

  /**
   * True while a refused ask is being put back. A refusal pauses the element,
   * and without this the play button would flicker to "Play" and back.
   */
  const probingRef = useRef(false);

  /**
   * Ask for sound as the reel starts, when the browser can say yes.
   *
   * Measured in Chrome with its real autoplay policy: a visitor who arrives by
   * clicking anything on the store brings that interaction with them, and the
   * reel is allowed to be heard straight away - the page only had to ask. On a
   * cold direct load nothing has been interacted with and Chrome refuses, and
   * refusing PAUSES the element. So the ask is made only when the document has
   * already been interacted with; a cold load waits for the first click, which
   * brings the sound in as before.
   *
   * Where navigator.userActivation does not exist the ask is made anyway, and
   * a refusal is put back: muted, still playing, waiting for a gesture.
   */
  const askForSound = () => {
    if (askedRef.current) return;
    askedRef.current = true;

    const el = videoRef.current;
    if (!el) return;
    if (!wantsSoundRef.current || soundGrantedRef.current) return;
    if (!atTopRef.current) return;

    const activation = (
      navigator as Navigator & { userActivation?: { hasBeenActive: boolean } }
    ).userActivation;
    if (activation && !activation.hasBeenActive) return;

    probingRef.current = true;
    soundGrantedRef.current = true;
    el.volume = 0;
    el.muted = false;

    const settle = (allowed: boolean) => {
      if (allowed && !el.paused && !el.muted) {
        probingRef.current = false;
        rampTo(volume);
        return;
      }
      // Refused. Put it back exactly as it was: muted, running, and waiting
      // for the first gesture to bring the sound in. probingRef clears on the
      // next play event, after the pause the refusal caused has been and gone.
      soundGrantedRef.current = false;
      el.muted = true;
      el.volume = volume;
      const resumed = el.play();
      if (resumed) resumed.catch(() => undefined);
    };

    const asked = el.play();
    if (asked)
      asked.then(
        () => settle(true),
        () => settle(false)
      );
    else settle(true);
  };

  const toggleSound = () => {
    const el = videoRef.current;
    if (!el) return;

    // Read from intent, not from the element: before the first gesture the
    // element is muted whatever the viewer wants, so asking it would invert
    // every first press.
    //
    // Unless this IS the first gesture. Until one arrives the reel is silent
    // while the button reads unmuted, so somebody pressing it is reaching for
    // sound, not away from it. Honour that and turn it on; after that it
    // toggles as you would expect.
    const nextMuted = soundGrantedRef.current ? wantsSoundRef.current : false;

    // A press is a gesture, which is all the browser was waiting for.
    soundGrantedRef.current = true;

    if (!nextMuted) {
      // Start silent and swell to the chosen level, so unmuting never lands as
      // a jolt. Muting is instant — nobody wants a slow fade when reaching for
      // the mute button.
      el.volume = 0;
      el.muted = false;
      // Then re-issue play() INSIDE the click. WebKit grants audio to an
      // element whose playback a person started, and the reel's did not: it
      // autoplays muted, which is the only way it is allowed to start at all,
      // so clearing `muted` on its own leaves it silent on iPhone and iPad in
      // every browser there, Chrome included, since they are all WebKit. The
      // landing page's videos play from a tap and were never affected, which
      // is why the fault looked specific to this page. Calling play() again
      // here is what promotes the element, and on a desktop browser it is a
      // no-op on something already playing.
      const promoted = el.play();
      if (promoted) promoted.catch(() => undefined);
      rampTo(volume);
    } else {
      if (rampRef.current) cancelAnimationFrame(rampRef.current);
      el.muted = true;
    }

    // The button expresses intent; the scroll rule above only ever suspends it
    wantsSoundRef.current = !nextMuted;
    writeSoundChoice(nextMuted ? 'off' : 'on');
    setIsMuted(nextMuted);

    // Coarse pointers get the slider on the same tap that turns sound on —
    // there is no hover to reveal it with, and a viewer who just unmuted is
    // exactly the person who wants to set the level. Muting puts it away.
    if (!hasHover) setVolumeOpen(!nextMuted);
  };

  /**
   * Sound is meant to be on, and a browser will not let it start that way.
   *
   * Audible autoplay is refused unless the page has been interacted with, and
   * the element is deliberately muted before its source is assigned so that it
   * is allowed to play at all — see the comment where that happens. Starting it
   * unmuted does not produce a reel with sound, it produces a reel that does
   * not run.
   *
   * The first gesture the page receives is the earliest moment audio can be
   * granted, so that is when it swells up. Any pointer, key or touch will do;
   * the viewer does not have to find the speaker button. It fires once.
   *
   * Three things it will not do: talk over a viewer who wants silence
   * (wantsSoundRef), start sound while the reel is scrolled away from — the
   * same rule the rest of the page follows through atTopRef — and jolt, since
   * it swells through rampTo exactly as the speaker button does.
   */
  useEffect(() => {
    const names: Array<keyof WindowEventMap> = [
      'pointerdown',
      'keydown',
      'touchstart'
    ];

    // A touch fires pointerdown and touchstart both; the guard means the
    // swell happens once however many of them arrive.
    let promoted = false;

    const promote = (event: Event) => {
      // A press on the sound controls is left alone. Those already say what
      // the viewer wants, and pointerdown arrives BEFORE click: acting here
      // unmuted the reel, and then the button's own handler found it unmuted
      // and muted it straight back. Sound took two presses to arrive.
      const target = event.target;
      if (target instanceof Element && target.closest('[data-volume-control]')) {
        return;
      }

      if (promoted) return;
      promoted = true;
      names.forEach((name) => window.removeEventListener(name, promote));

      // Nothing to do for somebody who wants silence, or who already has
      // sound - swelling again would dip a level they are already hearing.
      if (!wantsSoundRef.current || soundGrantedRef.current) return;

      // The gesture is the only thing the browser was holding out for.
      soundGrantedRef.current = true;

      const el = videoRef.current;
      if (!el) return;
      if (!atTopRef.current) return;

      // The same promotion the speaker button performs: clearing muted alone
      // leaves a WebKit element silent when a person did not start its
      // playback, and nobody started this one's.
      el.volume = 0;
      el.muted = false;
      const started = el.play();
      if (started) started.catch(() => undefined);
      rampTo(volume);
    };

    names.forEach((name) =>
      window.addEventListener(name, promote, { passive: true })
    );
    return () => {
      names.forEach((name) => window.removeEventListener(name, promote));
    };
  }, [volume]);

  /**
   * No loading state, ever — not even for the moment before the page data
   * arrives.
   *
   * There used to be a spinner here, and it was the first thing anyone saw:
   * the reel could not be drawn until /standby/page came back, because the
   * frame it opens on was delivered in that response. The frames now ship in
   * the bundle, so the reel paints on the very first render — ground colour,
   * then the opening frame, then the film — and the request only decides what
   * appears *below* it.
   *
   * The only thing still worth a full-page answer is a genuine failure, and
   * only once the request has actually finished. Until then there is nothing
   * to say and the reel says it.
   */
  if (!page && pageQuery.isError) return <NotFound />;

  return (
    <div className="relative w-full">
      {/* Pinned reel. Nothing sits on top of it but the controls — the film is
          the pitch, and a headline over it is just something in the way. */}
      <div
        // Painted the film's own opening colour rather than black. Whatever
        // happens before the first frame decodes, what is on screen is already
        // what the film opens on — there is no black plate to notice.
        className="sticky top-0 h-[100svh] w-full overflow-hidden"
        style={{
          visibility: stageCovered ? 'hidden' : 'visible',
          backgroundColor: STANDBY_STAGE_BG[theme]
        }}
      >
        <video
          ref={videoRef}
          poster={STANDBY_FIRST_FRAMES[`${theme}-${posterOrientation(variant)}`]}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          onPlay={() => {
            probingRef.current = false;
            setIsPlaying(true);
            askForSound();
          }}
          onPause={() => {
            // A refused ask pauses the element for a moment; that is not the
            // viewer pausing anything.
            if (probingRef.current) return;
            setIsPlaying(false);
          }}
          className="absolute inset-0 h-full w-full object-cover"
        />

        {/* Holds the last frame across a source swap — same object-cover as the
            video, so the held picture lines up exactly with the live one. */}
        <canvas
          ref={freezeRef}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          style={{ opacity: frozen ? 1 : 0 }}
        />

        <div className="absolute bottom-[20px] right-[20px] flex items-center gap-[8px]">
          <button
            type="button"
            onClick={togglePlay}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            className={CONTROL_CLASS}
          >
            <PlayIcon playing={isPlaying} />
          </button>
          {/* The slider lives with the speaker button and is revealed the way
              the device allows: hover on a mouse, the unmute tap on a phone or
              iPad. onFocus/onBlur are not decoration — without them the control
              is unreachable by keyboard, since there is no hover to trigger. */}
          <div
            data-volume-control
            ref={volumeWrapRef}
            className="relative"
            onPointerEnter={() => {
              if (!hasHover) return;
              cancelVolumeClose();
              setVolumeOpen(true);
            }}
            onPointerLeave={() => hasHover && scheduleVolumeClose()}
            onFocus={() => {
              cancelVolumeClose();
              setVolumeOpen(true);
            }}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                cancelVolumeClose();
                setVolumeOpen(false);
              }
            }}
          >
            {/* Fixed 44x124 and centred on the button, with a horizontal input
                rotated into place.

                It was built with writing-mode: vertical-lr, which Safari
                answers by giving the range input an enormous intrinsic size —
                on iPad the pill inflated into a dark circle wider than the
                button, hanging off the right edge of the screen. Rotating a
                normally-sized input avoids the question entirely: a transform
                does not affect layout, so the wrapper is exactly the size it
                says it is on every browser. -90deg puts maximum at the top. */}
            {/* The bridge. pointerenter/leave are computed over the DOM tree,
                not the layout box, so the floating panel already counts as
                "inside" the wrapper — the only dead zone is the 10px of empty
                air between them. This spans it, invisibly, and exists only
                while the panel is open so it can never intercept anything.
                A 1px overlap at each end avoids a sub-pixel seam at fractional
                zoom. Hover-only: on touch there is nothing to bridge. */}
            {volumeOpen && hasHover && (
              <div
                aria-hidden="true"
                className="absolute bottom-[calc(100%-1px)] left-1/2 h-[12px] w-[44px] -translate-x-1/2"
              />
            )}
            {volumeOpen && (
              <div className="absolute bottom-[calc(100%+10px)] left-1/2 flex h-[124px] w-[44px] -translate-x-1/2 items-center justify-center rounded-full bg-black/70 backdrop-blur-sm">
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.02}
                  value={sliderFromGain(volume)}
                  onChange={(e) => {
                    const gain = gainFromSlider(parseFloat(e.target.value));
                    setVolume(gain);
                    const el = videoRef.current;
                    if (el) {
                      // Dragging is its own ramp — follow the finger exactly,
                      // and cancel any swell still running underneath.
                      if (rampRef.current) cancelAnimationFrame(rampRef.current);
                      el.volume = gain;
                      // Sliding up from silence is a request for sound.
                      if (gain > 0 && el.muted) {
                        el.muted = false;
                        // Same WebKit promotion as toggleSound: the reel began
                        // playing without a gesture, so clearing muted alone
                        // leaves it silent on iOS.
                        const promoted = el.play();
                        if (promoted) promoted.catch(() => undefined);
                        wantsSoundRef.current = true;
                        soundGrantedRef.current = true;
                        writeSoundChoice('on');
                        setIsMuted(false);
                      }
                    }
                    try {
                      localStorage.setItem(VOLUME_KEY, String(gain));
                    } catch {
                      /* private mode */
                    }
                  }}
                  onPointerDown={() => {
                    draggingRef.current = true;
                    cancelVolumeClose();
                  }}
                  onPointerUp={(e) => endVolumeDrag(e.clientX, e.clientY)}
                  onPointerCancel={(e) => endVolumeDrag(e.clientX, e.clientY)}
                  aria-label="Volume"
                  className="h-[4px] w-[96px] -rotate-90 cursor-pointer accent-[#0ea5c9]"
                />
              </div>
            )}
            <button
              type="button"
              ref={speakerRef}
              onClick={toggleSound}
              aria-label={isMuted ? 'Unmute' : 'Mute'}
              aria-expanded={volumeOpen}
              aria-pressed={!isMuted}
              className={CONTROL_CLASS}
            >
              <SpeakerIcon muted={isMuted} />
            </button>
          </div>
        </div>

        {/* Scroll affordance, opposite the controls so the two never crowd. */}
        <div
          aria-hidden="true"
          className="absolute bottom-[26px] left-[24px] flex items-center gap-3 rounded-full bg-black/45 px-[14px] py-[7px] backdrop-blur-sm"
        >
          <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/80">
            {page?.hero.scrollHint ?? ""}
          </span>
          <span className="flex h-7 w-[18px] items-start justify-center rounded-full border border-white/50 p-[4px]">
            <span className="h-[6px] w-[2px] animate-standby-scroll rounded-full bg-white/80" />
          </span>
        </div>
      </div>

      {/* The story rides up over the reel as you scroll — that is the sticky
          stage doing the work, so the panel starts flush at the fold rather
          than pre-overlapping it. An overlap here sat on top of the media
          controls, which are bottom-anchored inside the stage, and hid them
          outright. */}
      {/* translateZ promotes this to its own layer so scrolling it does not
          force a repaint of the reel beneath. The lift used a 60px blur, which
          is expensive to redraw over live video every frame — a tighter shadow
          reads the same at the seam and costs a fraction of it. */}
      {/* Everything below the reel needs the payload, so it simply is not
          there until it arrives. No skeleton and no spinner: the reel fills the
          viewport, so there is nothing to see below the fold anyway, and a
          placeholder would only be a loading state wearing a costume. */}
      {page && (
      <div
        className="relative z-10 rounded-t-[32px] bg-surface-base shadow-[0_-8px_24px_rgba(0,0,0,0.22)]"
        style={{ transform: 'translateZ(0)', contain: 'paint' }}
      >
        <section className="px-6 pb-4 pt-16 text-center md:px-10 md:pt-24">
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-blue">
            {page.hero.eyebrow}
          </div>

          {/* Lifted from the Standby landing page, which is the logo of record:
              see HomePage in the standby repo's navigation/index.tsx.

              The SVG is its markup verbatim — including the third dot at 0.2
              opacity, which differs from the 0.3 the store's own StandbyMark
              uses. The weight is font-extrabold, not bold: Syne 800 is what the
              landing page sets and it is the whole reason this looked like a
              different logo rather than a differently spaced one.

              Sizes are in em where the landing page uses fixed pairs, because
              this heading is fluid at 13vw. The ratios are its: the mark runs
              1.08x the type size (52/48 on desktop, 34/32 on mobile) and the
              gap 0.33em (16/48, 12/32). */}
          <h1 className="mt-4 flex items-center justify-center gap-[0.33em] font-display text-[11vw] font-extrabold leading-none text-content-dark md:text-[88px]">
            <svg
              viewBox="0 0 64 64"
              fill="none"
              aria-hidden="true"
              className="h-[1.08em] w-[1.08em] shrink-0"
            >
              <circle cx="12" cy="32" r="9" fill="#0ea5c9" />
              <circle cx="32" cy="32" r="9" fill="currentColor" />
              <circle cx="52" cy="32" r="9" fill="currentColor" opacity="0.2" />
            </svg>
            {/* A name, so it never breaks.

                index.css sets hyphens:auto and overflow-wrap:break-word across
                the site — sensible for prose, ruinous for a wordmark. As a bare
                text node in a flex row it was an anonymous flex item whose
                min-content width became a syllable rather than the whole word,
                so the row was free to squeeze it and the logo came out as
                "Stand-" over "by". Its own element, nowrap, hyphens off: the
                lockup is now one indivisible thing at any width. */}
            <span className="shrink-0 whitespace-nowrap [hyphens:none]">
              {page.hero.wordmark}
            </span>
          </h1>

          {/* Standby's neutral grey, not the store's.
              text-content-secondary is #8D9EB8 in dark mode and #374151 in
              light — both biased toward blue to sit with the store's palette.
              The landing page sets #818185, which is neutral, and against a
              wordmark that is pure Syne the tint was enough to make this read
              as a different typeface even though the family and weight already
              matched. Stated literally, in both themes, exactly as the landing
              page does. */}
          <p className="mx-auto mt-5 max-w-[36ch] font-body text-[19px] text-[#818185] md:text-[24px]">
            {page.hero.tagline}
          </p>
          <p className="mx-auto mt-6 max-w-[58ch] font-body text-[15.5px] leading-[1.7] text-content-muted-alt">
            {page.intro}
          </p>
          <div className="mt-9">
            <CtaButton href={page.cta.href} label={page.cta.label} large />
          </div>
        </section>

        <div className="mx-auto grid max-w-[1180px] grid-cols-1 gap-5 px-6 pb-4 pt-10 md:grid-cols-2 md:px-10 md:pt-16">
          {page.cards.map((card) => (
            <Card key={card.id} card={card} />
          ))}
        </div>

        <section className="mt-6 border-t border-border-dark/[0.08] px-6 py-20 text-center md:px-10 md:py-28">
          <h2 className="font-display text-[32px] text-content-dark md:text-[48px]">
            {page.cta.heading}
          </h2>
          <p className="mx-auto mt-4 max-w-[46ch] font-body text-[17px] text-content-secondary">
            {page.cta.body}
          </p>
          <div className="mt-9">
            <CtaButton href={page.cta.href} label={page.cta.label} large />
          </div>
        </section>
      </div>
      )}
    </div>
  );
};

export default StandbyPageContainer;
